import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSegmentedModule } from 'ng-zorro-antd/segmented';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { take } from 'rxjs';
import { QuestionButtonComponent } from '../../../basic/buttons/question-button/question-button.component';
import { GoalService } from '../../../core/goal/goal.service';
import {
  InterviewAnswerMode,
  InterviewCreativity,
  InterviewPerformance,
  InterviewSubData,
  InterviewType,
} from '../../../core/interview/interview';
import { interviewLanguages } from '../../../core/interview/interview-language';
import { InterviewService } from '../../../core/interview/interview.service';
import { ResumeService } from '../../../core/resume/resume.service';
import { EnumUtils } from '../../../shared/utils/enum-utils';

@Component({
  selector: 'app-new-interview-copilot',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    NzIconModule,
    NzInputModule,
    NzButtonModule,
    NzTableModule,
    NzPopoverModule,
    NzModalModule,
    NzAlertModule,
    NzFormModule,
    NzRadioModule,
    NzSelectModule,
    NzSegmentedModule,

    QuestionButtonComponent,
  ],
  templateUrl: './new-interview-copilot.component.html',
  styleUrl: './new-interview-copilot.component.scss',
})
export class NewInterviewCopilotComponent {
  @Input() isNewInterviewVisible = false;
  @Output() isNewInterviewVisibleChange = new EventEmitter<boolean>();

  @Input() interviewType: InterviewType = InterviewType.normal;

  InterviewType = InterviewType;

  creativityOptions = EnumUtils.getAllValues(InterviewCreativity);
  answerModeOptions = EnumUtils.getAllValues(InterviewAnswerMode);

  languageList = interviewLanguages;

  programmingLanguageList = [
    'Java',
    'JavaScript',
    'TypeScript',
    'Python',
    'C++',
    'Go',
    'Ruby',
    'C#',
    'Rust',
    'PHP',
    'Swift',
  ];

  interviewRoundList = [
    'Phone Screen',
    'Technical/Skills Assessment',
    '1st Round',
    '2nd Round',
    '3rd Round',
    'Virtual Onsite',
    'Final Round',
    'Other',
  ];

  normalInterviewExplain = `
Typical interview where you'll answer questions related to your experience, skills, behavioral aspects, or any domain knowledge.

Select this if you anticipate behavioral or domain-related questions in your interview.
  `;

  codingInterviewExplain = `
A specialized interview focused on assessing your programming skills. You'll be given coding challenges to solve in real-time.

Select this if you anticipate programming or coding-related questions in your interview.  
`;

  creativityExplain = `
**Deterministic:** Produces precise and consistent answers, ideal for technical or factual questions where accuracy is key.

**Creative:** Generates creative and varied answers, perfect for open-ended or innovative questions where originality is valued.`;

  answerExplain = `
**Concise:** Provides short, to-the-point, and summarized answers in bullet points that are quick to read and easy to follow.

**Detailed:** Provides comprehensive answers with in-depth explanations, reasoning, examples, and context, useful for thorough understanding and responses.
`;

  validateForm: FormGroup<{
    interviewType: FormControl<InterviewType>;
    language: FormControl<string>;
    programmingLanguage: FormControl<string>;
    targetPosition: FormControl<string>;
    resume: FormControl<string>;
    interviewRound: FormControl<string>;
    creativity: FormControl<number>;
    answerMode: FormControl<number>;
    additionalDetails: FormControl<string>;
  }>;

  goalList = toSignal(this.goalService.goalList$(), { initialValue: [] });
  resumeList = toSignal(this.resumeService.resumeList$(), { initialValue: [] });

  constructor(
    private router: Router,
    private goalService: GoalService,
    private resumeService: ResumeService,
    private interviewService: InterviewService,
    private fb: NonNullableFormBuilder
  ) {
    this.validateForm = this.fb.group({
      interviewType: [InterviewType.normal, [Validators.required]],
      language: ['en', [Validators.required]],
      programmingLanguage: [this.programmingLanguageList[0], [Validators.required]],
      targetPosition: ['', [Validators.required]],
      resume: ['', [Validators.required]],
      interviewRound: ['First Round', [Validators.required]],
      creativity: [0, [Validators.required]],
      answerMode: [0, [Validators.required]],
      additionalDetails: [''],
    });
  }

  submitForm() {
    if (this.validateForm.valid) {
      this.isNewInterviewVisible = false;
      this.isNewInterviewVisibleChange.emit(this.isNewInterviewVisible);

      const goal = this.goalList().find((c) => c.id === this.validateForm.value.targetPosition);
      const resume = this.resumeList().find((c) => c.id === this.validateForm.value.resume);

      const subData: InterviewSubData = {
        interviewRound: this.validateForm.value.interviewRound!,
        goalId: this.validateForm.value.targetPosition!,
        goalCompany: goal?.subData.company ?? '',
        goalPosition: goal?.subData.position ?? '',
        resumeId: this.validateForm.value.resume!,
        resumeTitle: resume?.title ?? '',
        language: this.validateForm.value.language!,
        programmingLanguage: this.validateForm.value.programmingLanguage,
        performance: InterviewPerformance.Quality, // Hardcode Quality
        creativity: this.creativityOptions[this.validateForm.value.creativity!],
        answerMode: this.answerModeOptions[this.validateForm.value.answerMode!],
        additionalDetails: this.validateForm.value.additionalDetails,
      };

      const type = this.validateForm.value.interviewType!;

      this.interviewService
        .addInterview$(type, subData)
        .pipe(take(1))
        .subscribe(() => {
          this.router.navigate(['/interview']);
        });
    } else {
      Object.values(this.validateForm.controls).forEach((control) => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
    }
  }

  handleNewInterviewCancel() {
    this.isNewInterviewVisible = false;
    this.isNewInterviewVisibleChange.emit(this.isNewInterviewVisible);
  }
}
